import React, { useEffect, useCallback, useRef } from 'react'
import toast from 'react-hot-toast'

interface VersionInfo {
  version: string
  buildTime: string
  buildTimestamp: number
}

const VERSION_STORAGE_KEY = 'app_version_timestamp'
const CHECK_INTERVAL = 5 * 60 * 1000 // Verificar a cada 5 minutos

export function useVersionCheck() {
  const hasShownToast = useRef(false)

  const checkForUpdates = useCallback(async () => {
    try {
      // Busca o version.json com cache-busting
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store'
      })

      if (!response.ok) {
        return
      }

      const serverVersion: VersionInfo = await response.json()
      const storedTimestamp = localStorage.getItem(VERSION_STORAGE_KEY)

      // Se não há versão armazenada, armazena a atual (primeiro acesso)
      if (!storedTimestamp) {
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion.buildTimestamp.toString())
        return
      }

      const currentTimestamp = parseInt(storedTimestamp, 10)

      // Se a versão do servidor é mais recente, notifica o usuário
      if (serverVersion.buildTimestamp > currentTimestamp && !hasShownToast.current) {
        hasShownToast.current = true

        toast(
          (t) => (
            <div className="flex gap-4">
              {/* Ícone com gradiente */}
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/25">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-white mb-1">
                  Nova versão disponível
                </h4>
                <p className="text-sm text-gray-400 mb-3">
                  Uma atualização foi lançada com melhorias e correções.
                </p>

                {/* Botões */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      localStorage.setItem(VERSION_STORAGE_KEY, serverVersion.buildTimestamp.toString())
                      toast.dismiss(t.id)
                      window.location.reload()
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-medium rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40"
                  >
                    Atualizar agora
                  </button>
                  <button
                    onClick={() => {
                      toast.dismiss(t.id)
                      setTimeout(() => {
                        hasShownToast.current = false
                      }, 30 * 60 * 1000)
                    }}
                    className="px-4 py-2 bg-white/10 text-gray-300 text-sm font-medium rounded-lg hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    Depois
                  </button>
                </div>
              </div>

              {/* Botão fechar */}
              <button
                onClick={() => {
                  toast.dismiss(t.id)
                  setTimeout(() => {
                    hasShownToast.current = false
                  }, 30 * 60 * 1000)
                }}
                className="flex-shrink-0 text-gray-500 hover:text-gray-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ),
          {
            duration: Infinity,
            style: {
              background: 'linear-gradient(135deg, #12171F 0%, #0E1218 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '20px',
              maxWidth: '440px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
            },
          }
        )
      }
    } catch (error) {
      // Silenciosamente ignora erros de rede
      console.debug('Erro ao verificar versão:', error)
    }
  }, [])

  useEffect(() => {
    // Verifica imediatamente ao montar
    checkForUpdates()

    // Configura verificação periódica
    const interval = setInterval(checkForUpdates, CHECK_INTERVAL)

    // Também verifica quando a janela ganha foco (usuário volta à aba)
    const handleFocus = () => {
      checkForUpdates()
    }
    window.addEventListener('focus', handleFocus)

    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', handleFocus)
    }
  }, [checkForUpdates])

  // Função para forçar atualização da versão armazenada (útil após update)
  const updateStoredVersion = useCallback(async () => {
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store'
      })
      if (response.ok) {
        const serverVersion: VersionInfo = await response.json()
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion.buildTimestamp.toString())
      }
    } catch {
      // Ignora erros
    }
  }, [])

  return { checkForUpdates, updateStoredVersion }
}
